import React, { useState, useRef, useEffect } from 'react';
import { Skeleton } from '@mui/material';

interface LazyBackgroundImageProps {
  src: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  onLoad?: () => void;
  onError?: () => void;
}

export const LazyBackgroundImage: React.FC<LazyBackgroundImageProps> = ({
  src,
  placeholder = '/assets/images/placeholder/card-placeholder.png',
  className = '',
  style = {},
  children,
  onLoad,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const currentDivRef = divRef.current;

    if (!currentDivRef) return;

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            // Stop observing once the element is in view
            if (observerRef.current) {
              observerRef.current.unobserve(entry.target);
            }
          }
        });
      },
      {
        // Load image when it's 100px away from entering the viewport
        rootMargin: '100px 0px',
        threshold: 0.1,
      },
    );

    observerRef.current.observe(currentDivRef);

    return () => {
      if (observerRef.current && currentDivRef) {
        observerRef.current.unobserve(currentDivRef);
      }
    };
  }, []);

  useEffect(() => {
    if (isInView && src) {
      // Preload the image to know when it's ready
      const img = new Image();
      img.onload = () => {
        setImageLoaded(true);
        setIsLoading(false);
        onLoad?.();
      };
      img.onerror = () => {
        setHasError(true);
        setIsLoading(false);
        onError?.();
      };
      img.src = src;
    }
  }, [isInView, src, onLoad, onError]);

  // Only show the actual image after it's been preloaded, otherwise show placeholder
  const backgroundImageUrl = hasError ? placeholder : imageLoaded ? src : placeholder;

  return (
    <div
      ref={divRef}
      className={`relative ${className}`}
      style={{
        ...style,
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: style.backgroundSize || 'cover',
        backgroundPosition: style.backgroundPosition || 'center',
        transition: 'opacity 0.3s ease-in-out',
        opacity: imageLoaded ? 1 : 0.7,
      }}
    >
      {isLoading && isInView && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0.3,
          }}
        />
      )}
      {children}
    </div>
  );
};
